import { useId, type InputHTMLAttributes, type SelectHTMLAttributes } from "react";
import estilos from "./Campo.module.css";
import { claseSoloLectores } from "./SoloLectores";

interface Comunes {
  readonly rotulo: string;
  /** El rótulo existe para lectores de pantalla pero no se ve (buscadores con placeholder). */
  readonly rotuloOculto?: boolean;
  readonly ayuda?: string;
  readonly error?: string | null;
}

/**
 * Campo de texto con su rótulo, ayuda y error asociados (WCAG 1.3.1 y 3.3.1):
 * el error dice qué pasó y qué hacer, y queda anunciado con aria-describedby.
 */
export function Campo({
  rotulo,
  rotuloOculto,
  ayuda,
  error,
  className,
  ...resto
}: Comunes & InputHTMLAttributes<HTMLInputElement>) {
  const id = useId();
  const descritoPor = [ayuda && `${id}-ayuda`, error && `${id}-error`].filter(Boolean).join(" ") || undefined;
  return (
    <div className={[estilos.campo, className].filter(Boolean).join(" ")}>
      <label htmlFor={id} className={rotuloOculto ? claseSoloLectores : estilos.rotulo}>
        {rotulo}
      </label>
      <input
        id={id}
        className={estilos.control}
        aria-invalid={error ? true : undefined}
        aria-describedby={descritoPor}
        {...resto}
      />
      {ayuda && (
        <span id={`${id}-ayuda`} className={estilos.ayuda}>
          {ayuda}
        </span>
      )}
      {error && (
        <span id={`${id}-error`} className={estilos.error} role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export interface Opcion {
  readonly valor: string;
  readonly texto: string;
}

export function Selector({
  rotulo,
  rotuloOculto,
  opciones,
  className,
  ...resto
}: Comunes & { readonly opciones: readonly Opcion[] } & SelectHTMLAttributes<HTMLSelectElement>) {
  const id = useId();
  return (
    <div className={[estilos.campo, className].filter(Boolean).join(" ")}>
      <label htmlFor={id} className={rotuloOculto ? claseSoloLectores : estilos.rotulo}>
        {rotulo}
      </label>
      <select id={id} className={estilos.control} {...resto}>
        {opciones.map((o) => (
          <option key={o.valor} value={o.valor}>
            {o.texto}
          </option>
        ))}
      </select>
    </div>
  );
}
